import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import {
  CallResourceCitation,
  CallSentenceCitation,
  CitationMinuteNode,
  CitationMinuteNodeChunk,
  CitationMinuteNodes,
} from '../../../../../../backend/src/citations/minutes-citations-api.dto'
import { cleanCitedText } from '../../cited-passage-matching'

export type ResourceInfo = {
  id: number
  fileName: string
}

export type OpenDocumentEvent = {
  resourceId: number
  resourcePageNumber: number
  citedTexts: string[]
}

export type OpenMeetingEvent = {
  startSentenceId: string
  endSentenceId: string
}

export type CitationPluginOptions = {
  // Nullable because the call details endpoint degrades to null citations when it cannot build them.
  getCitations: () => CitationMinuteNodes | null
  getResources: () => ResourceInfo[]
  formatPageLabel: (page: number) => string
  formatMorePassages: (count: number) => string
  onOpenDocument: ((event: OpenDocumentEvent) => void) | null
  onOpenMeeting: ((event: OpenMeetingEvent) => void) | null
}

type CitationPluginState = {
  decorations: DecorationSet
  highlightedChunkIds: string[]
  highlightType: 'sentence' | 'resource' | null
  // The document the decorations were last built or mapped against. Used by the
  // `decorations` prop to detect (and self-heal) a set that has drifted out of
  // sync with the document being rendered.
  doc: ProseMirrorNode
  rebuildPending: boolean
}

type SetHighlightMeta = {
  type: 'SET_HIGHLIGHT'
  chunkIds: string[]
  citationType: 'sentence' | 'resource'
}

type ClearHighlightMeta = {
  type: 'CLEAR_HIGHLIGHT'
}

type RebuildMeta = {
  type: 'REBUILD'
}

type CitationMeta = SetHighlightMeta | ClearHighlightMeta | RebuildMeta

export const citationPluginKey = new PluginKey<CitationPluginState>('citation')

const CITATION_REBUILD_DEBOUNCE_MS = 150

type ChunkRange = {
  chunkId: string
  from: number
  to: number
}

type CitationEntry = {
  type: 'sentence' | 'resource'
  chunkId: string
  duplicateChunkIds: string[]
  sentenceCitation: CallSentenceCitation | null
  resourceCitation: CallResourceCitation | null
  resourceName: string | null
}

type ParagraphCitations = {
  paragraphEnd: number
  entries: CitationEntry[]
}

function findChunkRangesInMinuteNode(minuteNodePos: number, minuteNode: ProseMirrorNode): ChunkRange[] {
  const ranges: ChunkRange[] = []

  minuteNode.descendants((node, pos) => {
    if (!node.isText) {
      return true
    }

    const chunkMark = node.marks.find((mark) => mark.type.name === 'chunk') ?? null

    if (chunkMark === null) {
      return false
    }

    const chunkId = chunkMark.attrs['chunkId'] as string | null

    if (chunkId === null) {
      return false
    }

    ranges.push({
      chunkId,
      from: minuteNodePos + pos + 1,
      to: minuteNodePos + pos + 1 + node.nodeSize,
    })

    return false
  })

  return ranges
}

type ParagraphRange = {
  from: number
  end: number
}

function findParagraphRangesInMinuteNode(minuteNodePos: number, minuteNode: ProseMirrorNode): ParagraphRange[] {
  const ranges: ParagraphRange[] = []

  minuteNode.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') {
      return true
    }

    const absoluteFrom = minuteNodePos + pos + 1
    const absoluteTo = absoluteFrom + node.nodeSize

    ranges.push({ from: absoluteFrom, end: absoluteTo - 1 })
    return false
  })

  return ranges
}

function buildCitationEntries(
  citationNode: CitationMinuteNode,
  chunkRanges: ChunkRange[],
  resourceMap: Map<number, ResourceInfo>,
  paragraphRanges: ParagraphRange[]
): ParagraphCitations[] {
  const chunkRangeMap = new Map<string, ChunkRange>()
  for (const range of chunkRanges) {
    chunkRangeMap.set(range.chunkId, range)
  }

  const paragraphMap = new Map<number, ParagraphCitations>()

  for (const [, item] of Object.entries(citationNode.items)) {
    for (const [chunkId, chunk] of Object.entries(item.chunks)) {
      const matchingRange = chunkRangeMap.get(chunkId) ?? null

      if (matchingRange === null) {
        console.warn(`Could not find chunk with ID '${chunkId}' in the document, it might have been removed from the minutes.`)
        continue
      }

      const paragraphEnd = paragraphRanges.find((paragraph) => matchingRange.from >= paragraph.from && matchingRange.from < paragraph.end + 1)?.end ?? null

      if (paragraphEnd === null) {
        continue
      }

      const entries = buildEntriesForChunk(chunkId, chunk, resourceMap)

      if (entries.length === 0) {
        continue
      }

      const existing = paragraphMap.get(paragraphEnd) ?? null

      if (existing === null) {
        paragraphMap.set(paragraphEnd, { paragraphEnd, entries })
      } else {
        existing.entries.push(...entries)
      }
    }
  }

  return Array.from(paragraphMap.values())
}

function buildEntriesForChunk(chunkId: string, chunk: CitationMinuteNodeChunk, resourceMap: Map<number, ResourceInfo>): CitationEntry[] {
  const entries: CitationEntry[] = []

  for (const sentenceCitation of chunk.callSentenceCitations) {
    entries.push({
      type: 'sentence',
      chunkId,
      duplicateChunkIds: sentenceCitation.duplicateChunkIds,
      sentenceCitation,
      resourceCitation: null,
      resourceName: null,
    })
  }

  for (const resourceCitation of chunk.callResourceCitations) {
    const resource = resourceMap.get(resourceCitation.resourceId) ?? null

    if (resource === null) {
      console.warn(`Could not find resource with ID ${resourceCitation.resourceId} for call resource in chunk with ID ${chunkId}.`)
      continue
    }

    entries.push({
      type: 'resource',
      chunkId,
      duplicateChunkIds: resourceCitation.duplicateChunkIds,
      sentenceCitation: null,
      resourceCitation,
      resourceName: resource.fileName,
    })
  }

  return entries
}

function createWidgetDom(entries: CitationEntry[], labelStart: number, view: EditorView, options: CitationPluginOptions): HTMLElement {
  const group = document.createElement('span')
  group.className = 'citation-group'
  group.contentEditable = 'false'

  entries.forEach((entry, index) => {
    const badge = document.createElement('span')
    const label = labelStart + index

    badge.className = entry.type === 'sentence' ? 'citation-group-item citation-call-sentence' : 'citation-group-item citation-call-resource'
    badge.textContent = label.toString()

    const allChunkIds = [entry.chunkId, ...entry.duplicateChunkIds]

    badge.addEventListener('mouseenter', () => {
      view.dispatch(
        view.state.tr.setMeta(citationPluginKey, {
          type: 'SET_HIGHLIGHT',
          chunkIds: allChunkIds,
          citationType: entry.type,
        } satisfies CitationMeta)
      )

      if (entry.type === 'resource' && entry.resourceCitation !== null && entry.resourceName !== null) {
        showResourceTooltip(badge, entry.resourceName, entry.resourceCitation, options, view)
      }
    })

    badge.addEventListener('mouseleave', () => {
      view.dispatch(view.state.tr.setMeta(citationPluginKey, { type: 'CLEAR_HIGHLIGHT' } satisfies CitationMeta))

      removeResourceTooltip(view)
    })

    badge.addEventListener('click', () => {
      if (entry.type === 'sentence' && entry.sentenceCitation !== null && options.onOpenMeeting !== null) {
        options.onOpenMeeting({
          startSentenceId: entry.sentenceCitation.startSentenceId,
          endSentenceId: entry.sentenceCitation.endSentenceId,
        })
      } else if (entry.type === 'resource' && entry.resourceCitation !== null && options.onOpenDocument !== null) {
        options.onOpenDocument({
          resourceId: entry.resourceCitation.resourceId,
          resourcePageNumber: entry.resourceCitation.resourcePageNumber,
          citedTexts: entry.resourceCitation.citedTexts,
        })
      }
    })

    group.appendChild(badge)
  })

  return group
}

function getTooltipContainer(view: EditorView): HTMLElement {
  return view.dom.parentElement ?? view.dom
}

function getScrollContainer(view: EditorView): HTMLElement {
  return (view.dom.closest('.editor') as HTMLElement | null) ?? view.dom.parentElement ?? view.dom
}

const scrollHandlers = new WeakMap<EditorView, () => void>()

function withoutFileExtension(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '')
}

function createTooltipLine(className: string, text: string): HTMLElement {
  const line = document.createElement('div')
  line.className = className
  line.textContent = text

  return line
}

function showResourceTooltip(
  badge: HTMLElement,
  resourceName: string,
  resourceCitation: CallResourceCitation,
  options: CitationPluginOptions,
  view: EditorView
) {
  removeResourceTooltip(view)

  const container = getTooltipContainer(view)
  const [firstCitedText = null, ...otherCitedTexts] = resourceCitation.citedTexts

  const tooltip = document.createElement('div')
  tooltip.className = 'citation-tooltip'
  tooltip.appendChild(
    createTooltipLine('citation-tooltip-title', `${withoutFileExtension(resourceName)} (${options.formatPageLabel(resourceCitation.resourcePageNumber)})`)
  )

  if (firstCitedText !== null) {
    tooltip.appendChild(createTooltipLine('citation-tooltip-quote', `“${cleanCitedText(firstCitedText)}”`))
  }

  if (otherCitedTexts.length > 0) {
    tooltip.appendChild(createTooltipLine('citation-tooltip-more', options.formatMorePassages(otherCitedTexts.length)))
  }

  container.appendChild(tooltip)

  const badgeRect = badge.getBoundingClientRect()

  let left = badgeRect.left
  const top = badgeRect.bottom + 4

  tooltip.style.left = `${left}px`
  tooltip.style.top = `${top}px`

  const tooltipWidth = tooltip.offsetWidth
  const viewportWidth = window.innerWidth

  if (left + tooltipWidth > viewportWidth - 10) {
    left = viewportWidth - tooltipWidth - 10
  }

  if (left < 10) {
    left = 10
  }

  tooltip.style.left = `${left}px`
  tooltip.classList.add('visible')

  const scrollHandler = () => removeResourceTooltip(view)
  scrollHandlers.set(view, scrollHandler)
  getScrollContainer(view).addEventListener('scroll', scrollHandler, { once: true })
}

function removeResourceTooltip(view: EditorView) {
  const scrollHandler = scrollHandlers.get(view) ?? null

  if (scrollHandler !== null) {
    getScrollContainer(view).removeEventListener('scroll', scrollHandler)
    scrollHandlers.delete(view)
  }

  const container = getTooltipContainer(view)
  const existing = container.querySelector('.citation-tooltip')

  if (existing !== null) {
    existing.classList.remove('visible')
    existing.remove()
  }
}

function buildResourceMap(resources: ResourceInfo[]): Map<number, ResourceInfo> {
  const map = new Map<number, ResourceInfo>()
  for (const resource of resources) {
    map.set(resource.id, resource)
  }
  return map
}

// prosemirror-view's WidgetType.valid() unconditionally returns true, so a
// widget placed at a position that is not inside a textblock is never pruned
// and will corrupt the rendered DecorationSet. Validate positions ourselves.
function isValidWidgetPosition(doc: ProseMirrorNode, pos: number): boolean {
  if (pos < 0 || pos > doc.content.size) {
    return false
  }

  try {
    return doc.resolve(pos).parent.isTextblock
  } catch {
    return false
  }
}

function buildDecorationSet(
  doc: ProseMirrorNode,
  options: CitationPluginOptions,
  resourceMap: Map<number, ResourceInfo>,
  highlightedChunkIds: string[],
  highlightType: 'sentence' | 'resource' | null
): DecorationSet {
  const decorations: Decoration[] = []
  const highlightedSet = highlightedChunkIds.length > 0 ? new Set(highlightedChunkIds) : null
  const citations = options.getCitations()

  if (citations === null) {
    return DecorationSet.empty
  }

  doc.descendants((node, pos) => {
    if (node.type.name !== 'listItem') {
      return true
    }

    const nodeId = node.attrs['nodeId'] as string | null

    if (nodeId === null) {
      return true
    }

    const citationNode = citations[nodeId] ?? null

    if (citationNode === null) {
      return true
    }

    const chunkRanges = findChunkRangesInMinuteNode(pos, node)
    const paragraphRanges = findParagraphRangesInMinuteNode(pos, node)
    const paragraphCitations = buildCitationEntries(citationNode, chunkRanges, resourceMap, paragraphRanges)

    let labelCounter = 1

    for (const paragraphCitation of paragraphCitations) {
      if (!isValidWidgetPosition(doc, paragraphCitation.paragraphEnd)) {
        continue
      }

      const labelStart = labelCounter

      const widget = Decoration.widget(
        paragraphCitation.paragraphEnd,
        (currentView) => createWidgetDom(paragraphCitation.entries, labelStart, currentView, options),
        { key: `citation-${paragraphCitation.paragraphEnd}` }
      )
      decorations.push(widget)

      labelCounter += paragraphCitation.entries.length
    }

    if (highlightedSet !== null) {
      for (const range of chunkRanges) {
        if (highlightedSet.has(range.chunkId)) {
          const highlightClass =
            highlightType === 'resource' ? 'citation-highlight citation-call-resource-highlight' : 'citation-highlight citation-call-sentence-highlight'

          decorations.push(Decoration.inline(range.from, range.to, { class: highlightClass }))
        }
      }
    }

    return false
  })

  return DecorationSet.create(doc, decorations)
}

export function createCitationPlugin(options: CitationPluginOptions): Plugin {
  let resourceMap = buildResourceMap(options.getResources())

  function rebuildDecorations(doc: ProseMirrorNode, highlightedChunkIds: string[], highlightType: 'sentence' | 'resource' | null): CitationPluginState {
    return {
      decorations: buildDecorationSet(doc, options, resourceMap, highlightedChunkIds, highlightType),
      highlightedChunkIds,
      highlightType,
      doc,
      rebuildPending: false,
    }
  }

  return new Plugin<CitationPluginState>({
    key: citationPluginKey,

    state: {
      init(_, state) {
        return rebuildDecorations(state.doc, [], null)
      },

      apply(transaction, pluginState, _oldState, newState) {
        const meta = (transaction.getMeta(citationPluginKey) as CitationMeta | undefined) ?? null

        if (meta !== null) {
          if (meta.type === 'SET_HIGHLIGHT') {
            return rebuildDecorations(newState.doc, meta.chunkIds, meta.citationType)
          }

          if (meta.type === 'CLEAR_HIGHLIGHT') {
            return rebuildDecorations(newState.doc, [], null)
          }

          if (meta.type === 'REBUILD') {
            resourceMap = buildResourceMap(options.getResources())
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }
        }

        if (transaction.docChanged) {
          // Yjs sync transactions (initial load, minutes regeneration, and remote
          // edits) apply content as granular ReplaceSteps. Mapping the existing
          // decorations across them can leave citation widgets at positions that
          // no longer match the document being rendered; once merged with the
          // collaboration cursor decorations this corrupts the DecorationGroup and
          // crashes the view. Always rebuild fresh from the synced document.
          if (isChangeOrigin(transaction)) {
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }

          // Local edits: map immediately so widget positions stay consistent with
          // this tick's document, and debounce a full rebuild to re-derive the
          // citation grouping once the user stops typing.
          const mapped = pluginState.decorations.map(transaction.mapping, newState.doc)

          // Structural-local edits (delete/join/split) can relocate citation
          // widgets onto positions that are no longer textblocks. prosemirror-view
          // never prunes these (WidgetType.valid() always returns true), so a
          // drifted widget would corrupt the DecorationSet once merged with the
          // collaboration cursor decorations. Surviving zero-width decorations are
          // widgets (empty inline decorations are dropped during mapping); if any
          // landed somewhere invalid, rebuild fresh rather than ship the bad set.
          const hasDriftedWidget = mapped
            .find()
            .some((decoration) => decoration.from === decoration.to && !isValidWidgetPosition(newState.doc, decoration.from))

          if (hasDriftedWidget) {
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }

          return {
            decorations: mapped,
            highlightedChunkIds: pluginState.highlightedChunkIds,
            highlightType: pluginState.highlightType,
            doc: newState.doc,
            rebuildPending: true,
          }
        }

        return pluginState
      },
    },

    view() {
      let rebuildTimer: ReturnType<typeof setTimeout> | null = null

      return {
        update(view: EditorView) {
          const pluginState = citationPluginKey.getState(view.state) ?? null

          if (pluginState !== null && pluginState.rebuildPending) {
            if (rebuildTimer !== null) {
              clearTimeout(rebuildTimer)
            }

            rebuildTimer = setTimeout(() => {
              rebuildTimer = null

              const currentState = citationPluginKey.getState(view.state) ?? null

              if (currentState !== null && currentState.rebuildPending) {
                view.dispatch(view.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' } satisfies CitationMeta))
              }
            }, CITATION_REBUILD_DEBOUNCE_MS)
          }
        },

        destroy() {
          if (rebuildTimer !== null) {
            clearTimeout(rebuildTimer)
          }
        },
      }
    },

    props: {
      decorations(state) {
        const pluginState = citationPluginKey.getState(state) ?? null

        if (pluginState === null) {
          return DecorationSet.empty
        }

        // Defence in depth: if the stored set was built or mapped against a
        // different document than the one being rendered, rebuild on the spot so
        // a stale set can never reach the renderer.
        if (pluginState.doc !== state.doc) {
          return buildDecorationSet(state.doc, options, resourceMap, pluginState.highlightedChunkIds, pluginState.highlightType)
        }

        return pluginState.decorations
      },
    },
  })
}
